import React, { useEffect, useState } from "react";
import { backend, blogurl, categoryurl } from "../../../../path";
import { useParams, Link } from "react-router-dom";
import LoaderMain from "../../../layout/header/loader_sub";
import styled, { keyframes } from "styled-components";
import { FiCalendar, FiUser, FiArrowLeft } from "react-icons/fi";
import SEOHead from "../../../components/SEOHead";

const SEO_BLOG_DETAILS = {
  "101": {
    blog_id: 101,
    title: "Complete Guide to Buying Authentic Balaramapuram Elastic Kasavu Mundu",
    short_description: "Discover the heritage of Balaramapuram weaving, handloom gold zari craftsmanship, and modern elastic Kasavu Mundu wearing tips for Onam, Vishu, and weddings.",
    content: `<h2>The Heritage of Kerala Kasavu Mundu</h2><p>For centuries, the Kasavu Mundu has been the ultimate symbol of elegance and traditional pride in Kerala. Handwoven in the historic town of Balaramapuram near Thiruvananthapuram, authentic Kasavu Mundus feature unbleached natural cotton fabric paired with exquisite golden zari borders.</p><h3>Why Choose an Elastic Waist Kasavu Mundu?</h3><p>Traditional Kasavu Mundus require precise tucking and folding techniques that can slip during long wedding receptions or festival rituals. Modern <strong>Elastic Kasavu Mundus</strong> combine pre-stitched elastic waistbands with adjustable drawstrings, offering effortless wearability in seconds without compromising authentic handloom aesthetics.</p><h3>Key Features of Premium Elastic Kasavu Mundu</h3><ul><li><strong>100% Pure Fine Cotton:</strong> Soft, breathable, and suitable for all weather.</li><li><strong>Authentic Gold Zari:</strong> Premium metallic yarn with brilliant shine.</li><li><strong>Flexible Waistband:</strong> Stretches comfortably from 28 to 42 inches.</li></ul>`,
    image: "banner.png",
    author: "KeralaSpecial Heritage Team",
    created_at: "2026-07-28"
  },
  "102": {
    blog_id: 102,
    title: "Top 10 Health Benefits of Authentic Organic Kerala Spices",
    short_description: "Learn why Wayanad black pepper, Idukki green cardamom, and Kerala organic turmeric are world-renowned for aroma, flavor, and medicinal wellness.",
    content: `<h2>Why Kerala is Known as the Spice Garden of India</h2><p>Kerala's Western Ghats boast rich soil and tropical monsoon climate, producing the highest essential oil content in spices worldwide.</p><h3>1. Wayanad Organic Black Pepper</h3><p>Known as 'Black Gold', Wayanad pepper is rich in piperine, aiding digestion, boosting nutrient absorption, and fighting inflammation.</p><h3>2. Idukki Green Cardamom</h3><p>Fragrant and flavorful, authentic Idukki cardamom acts as a natural digestive, oral health remedy, and antioxidant powerhouse.</p>`,
    image: "banner1.png",
    author: "Spice Specialist",
    created_at: "2026-07-20"
  }
};

function BlogUserDetail() {
  const [data, setData] = useState(null);
  const [catData, setCatData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { id } = useParams();

  const decodeHTML = (html) => {
    const txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        if (SEO_BLOG_DETAILS[id]) {
          setData(SEO_BLOG_DETAILS[id]);
          return;
        }
        const response = await fetch(backend + `getblog/${id}`);
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const result = await response.json();
        if (result && result.title) {
          setData(result);
        } else if (SEO_BLOG_DETAILS["101"]) {
          setData(SEO_BLOG_DETAILS["101"]);
        }
      } catch (err) {
        setError(err);
        if (SEO_BLOG_DETAILS["101"]) {
          setData(SEO_BLOG_DETAILS["101"]);
        }
      } finally {
        setLoading(false);
      }
    };

    const fetchCatData = async () => {
      try {
        const response = await fetch(backend + "getcategorywithsubcategory");
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const result = await response.json();
        setCatData(result);
      } catch (err) {
        setError(err);
      }
    };

    fetchData();
    fetchCatData();
    window.scrollTo(0, 0);
  }, [id]);

  const blogSchema = data ? {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": data.title,
    "description": data.short_description || data.title,
    "image": [data.image ? (data.image.startsWith("http") ? data.image : `${blogurl}/${data.image}`) : "https://keralaspecialtours.com/storage/general/ks-logo.svg"],
    "datePublished": data.created_at || "2026-07-28",
    "author": {
      "@type": "Person",
      "name": data.author || "KeralaSpecial Team"
    },
    "publisher": {
      "@type": "Organization",
      "name": "KeralaSpecial.in",
      "logo": {
        "@type": "ImageObject",
        "url": "https://keralaspecialtours.com/storage/general/ks-logo.svg"
      }
    }
  } : null;

  return (
    <main className="main" id="main-section" style={{ backgroundColor: "#FDFBF7" }}>
      <SEOHead
        title={data?.title || "Kerala Product Insight"}
        description={data?.short_description || "Read insightful articles on authentic Kerala handlooms, spices, and culture at KeralaSpecial.in."}
        keywords={`${data?.title || 'Kerala Blog'}, Kerala Handlooms, Kerala Spices, Kasavu Mundu Guide`}
        type="article"
        schema={blogSchema}
      />
      <PageWrapper>
        <HeroSection>
          <BreadcrumbTrail>
            <Link to="/">Home</Link>
            <span>/</span>
            <Link to="/blog">Blogs</Link>
            <span>/</span>
            <span>{data?.title || "Blog Detail"}</span>
          </BreadcrumbTrail>
          <HeroContent>
            <HeroBadge>KeralaSpecial Insight</HeroBadge>
            <HeroTitle>{data?.title || "Discover Kerala"}</HeroTitle>
            <HeroSubtitle>
              Explore local secrets, artisan heritage, and traditions from the heart of Kerala.
            </HeroSubtitle>
          </HeroContent>
        </HeroSection>

        {error && <ParagraphError>Failed to load story details. Please try again later.</ParagraphError>}

        <ContentGrid>
          <ArticleColumn>
            {data?.thumbnail && (
              <LeadMedia>
                <img src={blogurl + data.thumbnail} alt={data.title} />
              </LeadMedia>
            )}

            {data?.content && (
              <RichText
                dangerouslySetInnerHTML={{
                  __html: decodeHTML(data.content),
                }}
              />
            )}

            {/* Visual Storyboard Image Gallery */}
            {(data?.image_1 || data?.image_2 || data?.image_3 || data?.image_4) && (
              <GallerySection>
                <GalleryTitle>Visual Storyboard</GalleryTitle>
                <GalleryGrid>
                  {[data?.image_1, data?.image_2, data?.image_3, data?.image_4]
                    .filter(Boolean)
                    .map((image, index) => (
                      <GalleryCard key={image + index} delay={index * 0.05}>
                        <img src={blogurl + image} alt={`${data?.title} gallery ${index + 1}`} />
                      </GalleryCard>
                    ))}
                </GalleryGrid>
              </GallerySection>
            )}
          </ArticleColumn>

          <Sidebar>
            <SidebarCard>
              <SidebarHeading>Quick Facts</SidebarHeading>
              <FactsList>
                <FactsItem>
                  <FactsLabel>Published</FactsLabel>
                  <FactsValue>
                    <FiCalendar style={{ marginRight: "6px", color: "#C5A880" }} />
                    {data?.created_at
                      ? new Date(data.created_at).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })
                      : "Recently"}
                  </FactsValue>
                </FactsItem>
                <FactsItem>
                  <FactsLabel>Written By</FactsLabel>
                  <FactsValue>
                    <FiUser style={{ marginRight: "6px", color: "#C5A880" }} />
                    {data?.author || "KeralaSpecial Team"}
                  </FactsValue>
                </FactsItem>
              </FactsList>
            </SidebarCard>

            <SidebarCard>
              <SidebarHeading>Continue Exploring</SidebarHeading>
              <ExploreList>
                {catData.slice(0, 4).map((item, i) => (
                  <ExploreItem key={item.category_slug} delay={i * 0.04}>
                    <Link to={`/category/${item.category_slug}`}>
                      <div className="explore-info">
                        <img src={categoryurl + item.logo} alt={item.category_name} />
                        <span>{item.category_name}</span>
                      </div>
                    </Link>
                  </ExploreItem>
                ))}
              </ExploreList>
            </SidebarCard>

            <BackLink to="/blog">
              <FiArrowLeft /> Back to all stories
            </BackLink>
          </Sidebar>
        </ContentGrid>

        {loading && <LoaderMain />}
      </PageWrapper>
    </main>
  );
}

export default BlogUserDetail;

// --- STYLED COMPONENTS ---

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInScale = keyframes`
  from {
    opacity: 0;
    transform: scale(0.97);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const PageWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px 96px;
  display: flex;
  flex-direction: column;
  gap: 56px;
`;

const HeroSection = styled.section`
  margin-top: 48px;
  background: linear-gradient(135deg, #F2ECE1 0%, #FDFBF7 100%);
  border-radius: 28px;
  padding: 48px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  border: 1px solid rgba(197, 168, 128, 0.25);
  box-shadow: 0 16px 36px rgba(45, 31, 24, 0.02);
  animation: ${fadeInScale} 0.6s ease forwards;

  @media screen and (max-width: 768px) {
    padding: 32px 24px;
    margin-top: 24px;
  }
`;

const BreadcrumbTrail = styled.nav`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-family: "Jost", sans-serif;
  font-size: 13.5px;
  color: #888888;

  a {
    color: #123524;
    font-weight: 500;
    transition: color 0.2s;

    &:hover {
      color: #C5A880;
    }
  }

  span {
    color: #C5A880;
  }

  span:last-child {
    color: #2D1F18;
    font-weight: 600;
  }
`;

const HeroContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  animation: ${fadeInUp} 0.7s ease forwards;
`;

const HeroBadge = styled.span`
  display: inline-flex;
  align-items: center;
  background: rgba(18, 53, 36, 0.08);
  color: #123524;
  padding: 6px 14px;
  border-radius: 999px;
  font-family: "Jost", sans-serif;
  font-size: 11px;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  font-weight: 600;
  width: fit-content;
  border: 1px solid rgba(197, 168, 128, 0.25);
`;

const HeroTitle = styled.h1`
  margin: 0;
  font-family: "Playfair Display", serif;
  font-size: 38px;
  font-weight: 700;
  line-height: 1.25;
  color: #123524;

  @media screen and (max-width: 768px) {
    font-size: 28px;
  }
`;

const HeroSubtitle = styled.p`
  margin: 0;
  font-family: "Poppins", sans-serif;
  color: #2D1F18;
  opacity: 0.85;
  font-size: 15px;
  line-height: 1.6;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1.7fr 1.0fr;
  gap: 40px;

  @media screen and (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 48px;
  }
`;

const ArticleColumn = styled.article`
  display: flex;
  flex-direction: column;
  gap: 36px;
`;

const LeadMedia = styled.div`
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 12px 28px rgba(45, 31, 24, 0.04);
  border: 1px solid rgba(197, 168, 128, 0.2);
  animation: ${fadeInUp} 0.6s ease forwards;

  img {
    width: 100%;
    height: auto;
    max-height: 500px;
    object-fit: cover;
    display: block;
  }
`;

const RichText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  font-family: "Poppins", sans-serif;
  font-size: 16px;
  line-height: 1.85;
  color: #2D1F18;
  animation: ${fadeInUp} 0.7s ease forwards;

  h1, h2, h3, h4 {
    font-family: "Playfair Display", serif;
    color: #123524;
    margin-top: 32px;
    margin-bottom: 12px;
    font-weight: 700;
  }

  h1 { font-size: 28px; }
  h2 { font-size: 24px; }
  h3 { font-size: 20px; }

  p {
    margin: 0 0 16px 0;
    opacity: 0.9;
  }

  a {
    color: #123524;
    font-weight: 600;
    text-decoration: underline;
    text-decoration-color: #C5A880;
    text-underline-offset: 4px;
    transition: color 0.2s;

    &:hover {
      color: #C5A880;
    }
  }

  blockquote {
    border-left: 4px solid #C5A880;
    padding: 16px 24px;
    font-style: italic;
    background: #F2ECE1;
    border-radius: 0 16px 16px 0;
    margin: 28px 0;
    color: #2D1F18;
    opacity: 0.95;

    p {
      margin: 0;
    }
  }

  img {
    border-radius: 20px;
    margin: 28px 0;
    width: 100%;
    height: auto;
    box-shadow: 0 8px 20px rgba(45, 31, 24, 0.04);
    border: 1px solid rgba(197, 168, 128, 0.15);
  }

  ul, ol {
    padding-left: 24px;
    margin: 0 0 20px 0;

    li {
      margin-bottom: 8px;
      opacity: 0.9;
    }
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 28px 0;
    font-size: 15px;

    th, td {
      border: 1px solid rgba(197, 168, 128, 0.25);
      padding: 12px 16px;
      text-align: left;
    }

    th {
      background-color: #F2ECE1;
      color: #123524;
      font-family: "Jost", sans-serif;
      font-weight: 600;
    }

    tr:nth-child(even) {
      background-color: #FDFBF7;
    }
  }
`;

const GallerySection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-top: 24px;
  border-top: 1px solid rgba(197, 168, 128, 0.2);
`;

const GalleryTitle = styled.h3`
  margin: 0;
  font-family: "Playfair Display", serif;
  font-size: 22px;
  font-weight: 700;
  color: #123524;
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;

  @media screen and (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const GalleryCard = styled.div`
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 8px 20px rgba(45, 31, 24, 0.03);
  border: 1px solid rgba(197, 168, 128, 0.18);
  height: 200px;
  transform: translateY(15px);
  opacity: 0;
  animation: ${fadeInUp} 0.6s ease forwards;
  animation-delay: ${({ delay }) => delay}s;
  transition: all 0.3s ease;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 28px rgba(45, 31, 24, 0.06);

    img {
      transform: scale(1.03);
    }
  }
`;

const Sidebar = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 28px;
`;

const SidebarCard = styled.div`
  background: #ffffff;
  border-radius: 24px;
  padding: 28px;
  border: 1px solid rgba(197, 168, 128, 0.2);
  box-shadow: 0 8px 24px rgba(45, 31, 24, 0.015);
  display: flex;
  flex-direction: column;
  gap: 20px;
  animation: ${fadeInRight} 0.6s ease forwards;
`;

const SidebarHeading = styled.h4`
  margin: 0;
  font-family: "Playfair Display", serif;
  font-size: 18px;
  font-weight: 700;
  color: #123524;
  position: relative;
  padding-bottom: 8px;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 32px;
    height: 2px;
    background: #C5A880;
  }
`;

const FactsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FactsItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(197, 168, 128, 0.1);

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const FactsLabel = styled.span`
  font-family: "Jost", sans-serif;
  font-size: 11px;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: #C5A880;
  font-weight: 600;
`;

const FactsValue = styled.span`
  display: flex;
  align-items: center;
  font-family: "Poppins", sans-serif;
  font-size: 15px;
  color: #2D1F18;
  font-weight: 500;
`;

const ExploreList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ExploreItem = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-radius: 12px;
  background: #FDFBF7;
  border: 1px solid rgba(197, 168, 128, 0.12);
  transition: all 0.3s ease;
  transform: translateY(15px);
  opacity: 0;
  animation: ${fadeInUp} 0.6s ease forwards;
  animation-delay: ${({ delay }) => delay}s;

  &:hover {
    background: #F2ECE1;
    transform: translateY(-2px);
    border-color: rgba(197, 168, 128, 0.3);
  }

  a {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: 12px;
    color: #2D1F18;
    font-family: "Jost", sans-serif;
    font-weight: 500;
    font-size: 14px;

    .explore-info {
      display: flex;
      align-items: center;
      gap: 12px;
    }
  }

  img {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    object-fit: cover;
    border: 1px solid rgba(197, 168, 128, 0.25);
  }

  span {
    font-size: 14px;
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 30px;
  background: #123524;
  color: #FDFBF7;
  font-family: "Jost", sans-serif;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.3s;
  box-shadow: 0 4px 12px rgba(18, 53, 36, 0.15);
  align-self: flex-start;

  &:hover {
    background: #C5A880;
    color: #FDFBF7;
    transform: translateY(-2px);
  }
`;

const ParagraphError = styled.p`
  margin: 0;
  font-family: "Poppins", sans-serif;
  color: #b91c1c;
  background: #fee2e2;
  border-radius: 12px;
  padding: 14px 20px;
  font-weight: 500;
  font-size: 14px;
  animation: ${fadeInUp} 0.5s ease forwards;
`;
